/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : Sales spikes list
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { Modal } from "bootstrap";
import { useSelector } from "react-redux";

//Custom components
import { DataTableComponent } from "../../../Component/DataTable";
import { APIService } from "../../../Service";
import { DownloadButton, PrimaryButton, TextButton } from "../../../Component/Buttons";
import { ConfirmationModal, PauseRewardModal } from "../../../Component/Modal";
import { CustomDatePicker, CustomSearchInput, CustomSelectBox, DateRangePicker } from "../../../Component/FormElements";
import { Loader, OverLay } from "../../../Component/Others";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../../Component/Icon";

const ITEMS_PER_PAGE = 20;

const SALES_SPIKE_SORT_FIELDS = [
    'id',
    'date',
    'sales_specialist',
    'invoice_number',
    'store_name',
    'product_name',
    'volume',
    'sales_amount',
    'spike_percent',
    'reward_status',
    'status',
];

const SalesSpikesList = () => {
    const today = new Date();
    const navigate = useNavigate();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState([startOfMonth(today), today]);
    const [loader, setLoader] = useState(true);
    const [listData, setListData] = useState({});
    const [downloadLoader, setDownloadLoader] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    const [toggleFilter, setToggleFilter] = useState(false);

    const [clearFilter, setClearFilter] = useState(false);
    const [selectedSpecialist, setSelectedSpecialist] = useState(null);
    const [selectedSpikes, setSelectedSpikes] = useState(null);
    const [selectedSalesAmount, setSelectedSalesAmount] = useState(null);
    const [rewardStatus, setRewardStatus] = useState(null);
    const [status, setStatus] = useState(null);


    const [startDate, setStartDate] = useState(null);
    const [startDateToggle, setStartDateToggle] = useState(false);

    const [endDate, setEndDate] = useState(null);
    const [endDateToggle, setEndDateToggle] = useState(false);

    const [flagModal, setFlagModal] = useState(false);
    const [flagStatus, setFlagStatus] = useState(false);
    const [buttonLoader, setButtonLoader] = useState();

    const [filterData, setFilterData] = useState({});
    const [filterLoader, setFilterLoader] = useState(true);

    const [spikeId, setSpikeId] = useState(null);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        _getFilterData();
    }, []);

    useEffect(() => {
        if (flagModal === true) {
            const modal = new Modal(document.getElementById('confirm-modal'));
            modal.show();
        }
    }, [flagModal]);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getSalesSpikes();
    }, [currentPage, clearFilter, searchValue]);

    // handle paginate store list 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !SALES_SPIKE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoader(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getSalesSpikes(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleClearAll = () => {
        setClearFilter(true);
        setLoader(true);
        setCurrentPage(1);
        setSortField(null);
        setSortDirection(null);

        // setSelectedSpecialist(filterData.sales_specialists[0]);
        setSelectedSpikes(filterData.spike_percent_ranges[0]);
        // setSelectedSalesAmount(filterData.sales_amount_ranges[0]);
        setRewardStatus(filterData.reward_status[0]);
        setStatus(filterData.status[0]);

        setEndDate(null);
        setStartDate(null);
    };


    // Table columns
    const TABLE_COLUMNS = [
        {
            name: 'Date',
            id: 'date',
            sortField: 'date',
            selector: row => row.date || '-',
            cell: row => <>{row.date || '-'}</>,
            format: row => row.date,
            width: "100px",
            sortable: true
        },
        {
            name: 'Sales specialist',
            id: 'sales_specialist',
            sortField: 'sales_specialist',
            selector: row => row.sales_specialist || "-",
            cell: row => <>{row.sales_specialist || "-"}</>,
            width: "260px",
            sortable: true
        },
        {
            name: 'Invoice number',
            id: 'invoice_number',
            sortField: 'invoice_number',
            selector: row => row.invoice_number || "-",
            cell: row => <>{row.invoice_number || "-"}</>,
            width: "170px",
            sortable: true
        },
        {
            name: 'Volume',
            id: 'volume',
            sortField: 'volume',
            selector: row => row.volume || "-",
            cell: row => <>{row.volume || "-"}</>,
            width: "100px",
            sortable: true
        },
        // {
        //     name: 'Sales amount',
        //     selector: row => row.sales_amount || "-",
        //     width: "140px",
        //     sortable: true
        // },
        {
            name: 'Spike %',
            id: 'spike_percent',
            sortField: 'spike_percent',
            selector: row => row.spike_percent || "-",
            width: "100px",
            sortable: true
        },
        {
            name: 'Rewards',
            id: 'reward_status',
            sortField: 'reward_status',
            selector: row => row.reward_status,
            cell: row => <>
                {row.reward_status ?
                    <span className={`${row.reward_status.toLowerCase() === "active" ? "e-text-philippine-green" : "e-text-bright-orange"}  e-font-weight-500`}>{row.reward_status}</span>
                    : "-"}
            </>,
            width: "130px",
            sortable: true
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            selector: row => row.status,
            cell: row => <>
                {row.status ?
                    <span className={`${row.status.toLowerCase() === "active" ? "e-text-philippine-green" : "e-text-bright-orange"}  e-font-weight-500`}>{row.status}</span>
                    : "-"}
            </>,
            width: "100px",
            sortable: true
        },
        {
            name: 'Actions',
            cell: row =>
                <div className="d-flex align-items-center gap-3">
                    <TextButton name={row.status?.toLowerCase() === "active" ? "Flag" : "Unflag"}
                        className="e-font-weight-400"
                        onPress={() => {
                            setFlagModal(true);
                            setSpikeId(row.id);
                        }} />

                    <TextButton name="View"
                        className=" e-font-weight-400"
                        icon={true}
                        onPress={() => {
                            navigate("detail", {
                                state: {
                                    id: row.id
                                }
                            })
                        }}
                    />
                </div>,
            width: "150px"
        },
    ];

    // API - Get all sales spikes
    const _getSalesSpikes = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/sales-spike/list';
        let data = {
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "start_date": startDate !== null ? format(startDate, 'yyyy-MM-dd') : null,  // Required
            "end_date": endDate !== null ? format(endDate, 'yyyy-MM-dd') : null,    // Required
            "store_id": null,             // Optional
            "product_id": null,             // Optional
            // "sales_specialist_id": selectedSpecialist?.value || null,
            "spike_percent_range": selectedSpikes?.value || null,
            // "sales_amount_range": selectedSalesAmount?.value || null,
            "reward_status": rewardStatus?.value || null,
            "status": status?.value || null,
            "search_query": searchValue,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setListData(response.data);
            } else {
                setListData({});
            }
            setLoader(false);
            setClearFilter(false);
        });
    };

    // API - Get filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = "CA/sales-spike/filter-data";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                let updated_data = [
                    { value: null, label: "All" },
                    ...response.data.sales_specialists.map(data => ({
                        value: data.id,
                        label: data.name
                    }))
                ];

                setFilterData({
                    ...response.data,
                    sales_specialists: updated_data
                });


                // setSelectedSpecialist(updated_data[0]);
                setSelectedSpikes(response.data.spike_percent_ranges[0]);
                // setSelectedSalesAmount(response.data.sales_amount_ranges[0]);
                setRewardStatus(response.data.reward_status[0]);
                setStatus(response.data.status[0]);
            } else {
            }
            setFilterLoader(false);
        });
    };


    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/sales-spike/download";
        let data = {
            "start_date": startDate !== null ? format(startDate, 'yyyy-MM-dd') : null,
            "end_date": endDate !== null ? format(endDate, 'yyyy-MM-dd') : null,
            "store_id": null,
            "product_id": null,

            // "sales_specialist_id": selectedSpecialist?.value || null,
            "spike_percent_range": selectedSpikes?.value || null,
            // "sales_amount_range": selectedSalesAmount?.value || null,
            "reward_status": rewardStatus?.value || null,
            "status": status?.value || null,
            "search_query": searchValue,
            "sort_by": sortField ? [sortField] : null,
            "order": sortDirection ? [sortDirection] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    // API - Download data
    const _flagToggle = () => {
        setButtonLoader(true);
        let is_post = true;
        let url = "CA/sales-spike/toggle-flag";
        let data = {
            "id": spikeId
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setFlagStatus(true);

                setTimeout(() => {
                    setFlagModal(false);

                    _getSalesSpikes(1);
                    setLoader(true);
                }, 200);
            } else {
                setFlagStatus(false);

                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setButtonLoader(false);
        });
    };


    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                    Manage Fraud
                </h1>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-border-1px-platinum-dark e-admin-box-shadow py-4 e-border-radius-16'>
                    <div className='px-4 pb-3 d-flex align-items-center justify-content-end'>
                        {/* <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-18 e-font-weight-500 line-height-18px' >
                            Overview
                        </h4> */}

                        {/* <div className="d-flex align-items-center gap-4">
                            <DateRangePicker
                                placeholder="Select"
                                calendarWrapperClass="end-0"
                                className="e-border-radius-8 px-3"
                                value={selectedDate}
                                maxDate={new Date}
                                activePresentId={4}
                                handleClear={() => setSelectedDate(null)}
                                handleChange={(date) => setSelectedDate(date)}
                                handleApply={() => _getSalesSpikes(1)} />

                            <SecondaryButton name={isPause ? "Resume rewards" : "Pause rewards"}
                                className="e-border-radius-8"
                                disabled={Object.keys(listData).length === 0 || listData?.brands?.length == 0}
                                padding="padding-10px-tb padding-12px-lr"
                                onPress={() => setPauseModal(true)}
                            />
                        </div> */}


                        <div className='d-flex justify-content-end align-items-center gap-4'>
                            <DownloadButton loader={downloadLoader}
                                disabled={loader || Object.keys(listData).length === 0 || listData?.data?.length === 0}
                                onClick={_download} />

                            <div className='position-relative'>
                                <CustomSearchInput placeholder="Search by name, ID or store"
                                    type="text"
                                    require
                                    value={searchValue}
                                    className="w-320px py-2"
                                    iconClass="top-9px"
                                    labelStyle="e-font-weight-500"
                                    handleChange={(e) => setSearchValue(e)}
                                    handleClear={() => {
                                        setSearchValue("");
                                        setLoader(true);
                                    }} />
                            </div>

                            <div className={filterLoader ? "pe-none opacity-75" : ""}>
                                <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                    draggable={false}
                                    height={40}
                                    width={40}
                                    className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                    alt='filter'
                                    onClick={() => setToggleFilter(!toggleFilter)} />
                            </div>

                        </div>
                    </div>


                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4'>
                            <div className='row row-gap-4'>
                                {/* <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Sales specialist"
                                        value={selectedSpecialist}
                                        options={filterData.sales_specialists}
                                        // loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedSpecialist(value)}
                                    />
                                </div> */}

                                <div className='col-5'>
                                    <p className='mb-0 e-text-dark-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px d-flex'>
                                        Date range
                                    </p>

                                    <div className="d-flex align-items-end gap-3">
                                        <div className="w-100">
                                            <div className='pt-1 row'>
                                                <div className='position-relative '>
                                                    <div className={`${startDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} w-100 e-display-flex gap-0-4px justify-content-between e-border-radius-4 p-2 cursor-pointer e-bg-white`}
                                                        onClick={() => setStartDateToggle(!startDateToggle)}>

                                                        {startDate !== null ?
                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                                {format(startDate, 'PP')}
                                                            </p>
                                                            :
                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                                From
                                                            </p>
                                                        }
                                                        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
                                                            {startDate === null ?
                                                                <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                    draggable={false}
                                                                    width={16}
                                                                    height={16}
                                                                    alt='calender-icon' />

                                                                :
                                                                <Icon icon="close-icon"
                                                                    className='cursor-pointer d-flex'
                                                                    size={16}
                                                                    onPress={() => {
                                                                        setStartDate(null);
                                                                        setEndDate(null);
                                                                    }} />
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {startDateToggle &&
                                                <>
                                                    <OverLay className="e-bg-overlay " isOpen={startDateToggle} onClose={() => setStartDateToggle(!startDateToggle)} />
                                                    <CustomDatePicker selected={startDate}
                                                        className='e-day-picker w-358px position-absolute e-bg-white e-border-radius-16 z-index-99'
                                                        disableFuture={true}
                                                        onSelect={(day) => {
                                                            setStartDate(day);
                                                            setEndDate(null);
                                                            setStartDateToggle(false);
                                                        }} />
                                                </>
                                            }
                                        </div>

                                        <div className="w-100">
                                            <div className='pt-1 row'>
                                                <div className='position-relative '>
                                                    <div className={`${endDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} w-100 e-display-flex gap-0-4px justify-content-between e-border-radius-4 p-2 cursor-pointer e-bg-white`}
                                                        onClick={() => setEndDateToggle(!endDateToggle)}>

                                                        {endDate !== null ?
                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                                {format(endDate, 'PP')}
                                                            </p>
                                                            :
                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                                To
                                                            </p>
                                                        }
                                                        <div className='d-flex' onClick={(e) => e.stopPropagation()}>
                                                            {endDate === null ?
                                                                <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                    draggable={false}
                                                                    width={16}
                                                                    height={16}
                                                                    alt='calender-icon' />

                                                                :
                                                                <Icon icon="close-icon"
                                                                    className='cursor-pointer d-flex'
                                                                    size={16}
                                                                    onPress={() => setEndDate(null)} />
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {endDateToggle &&
                                                <>
                                                    <OverLay className="e-bg-overlay " isOpen={endDateToggle} onClose={() => setEndDateToggle(!endDateToggle)} />
                                                    <CustomDatePicker selected={endDate}
                                                        className='e-day-picker w-358px position-absolute e-bg-white e-border-radius-16 z-index-99'
                                                        disableFuture={true}
                                                        minDate={startDate}
                                                        onSelect={(day) => {
                                                            setEndDate(day);
                                                            setEndDateToggle(false);
                                                        }} />
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>

                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Spike %"
                                        value={selectedSpikes}
                                        options={filterData.spike_percent_ranges}
                                        // loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedSpikes(value)}
                                    />
                                </div>

                                {/* <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Sales amount"
                                        value={selectedSalesAmount}
                                        options={filterData.sales_amount_ranges}
                                        // loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedSalesAmount(value)}
                                    />
                                </div> */}

                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Rewards"
                                        value={rewardStatus}
                                        options={filterData.reward_status}
                                        // loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setRewardStatus(value)}
                                    />
                                </div>

                            </div>

                            <div className="row row-gap-4 pt-4">

                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Status"
                                        value={status}
                                        options={filterData.status}
                                        // loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setStatus(value)}
                                    />
                                </div>

                                <div className='col-md-3 col-sm-4 col-6 ms-auto mt-auto'>
                                    <div className='d-flex gap-3 align-items-center '>
                                        <TextButton name="Clear all"
                                            onPress={_handleClearAll} />
                                        <PrimaryButton name="Filter now"
                                            className="px-3 py-1 e-border-radius-8"
                                            onPress={() => {
                                                setLoader(true);
                                                _getSalesSpikes(1);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {
                        loader ?
                            <Loader />
                            :
                            Object.keys(listData).length !== 0 && listData?.data?.length !== 0 ?
                                <div className={`e-table no-strip`}>
                                    <DataTableComponent
                                        key={`sales-spikes-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        pagination={true}
                                        data={listData.data}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationServer={true}
                                        paginationTotalRows={listData.total_count}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                <div className='h-100vh py-5 my-5'>
                                    <EmptyScreen message="No data found" />
                                </div>
                    }
                </div >
            </div >


            {flagModal === true &&
                <ConfirmationModal title={"Flag this record?"}
                    description={"Flagging helps you keep track of records that may need further review. No rewards or payouts will be affected."}
                    primaryButtonLabel={"Flag for review"}
                    loader={buttonLoader}
                    status={flagStatus}
                    handleClose={() => {
                        setTimeout(() => {
                            setFlagModal(false);
                            setFlagStatus(false);
                            setSpikeId(null);
                        }, 200);
                    }}

                    handleSuccess={_flagToggle}
                />
            }
        </section >
    )
}

export default SalesSpikesList;
